import { useState, useEffect } from "react"
import { Button } from "~/react/ui/button"
import { SheetHeader, SheetTitle, SheetFooter } from "~/react/ui/sheet"
import {
  Tabs,
  TabsList,
  TabsListItem,
  TabsTrigger,
  TabsContent,
} from "~/react/ui/tabs"
import { Icon } from "~/react/ui/icon"
import { Avatar, AvatarImage, AvatarFallback } from "~/react/ui/avatar"
import defaultLeadAvatarUrl from "~/images/default_lead_avatar.svg"
import { Tag } from "~/react/ui/tag"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuAction,
} from "~/react/ui/dropdown-menu"
import EmailAccountHealthTab from "./tabs/email_account_health_tab"
import HealthTab from "./tabs/health_tab"
import EmailAccountUsageTab from "./tabs/email_account_usage_tab"
import EmailAccountProfileTab from "./tabs/email_account_profile_tab"
import { CredentialsModal } from "./credentials_modal"
import { getCsrfToken } from "~/lib/utils"
import { useHealthCheck } from "~/react/hooks/useHealthCheck"
import type { EmailAccount } from "~/react/components/email_accounts/email_accounts_filters"

type EmailAccountSidepanelProps = {
  account: EmailAccount
  onAccountUpdated?: (updatedAccount: EmailAccount) => void
}

export default function EmailAccountSidepanel({
  account,
  onAccountUpdated,
}: EmailAccountSidepanelProps) {
  const [isSaving, setIsSaving] = useState(false)
  const [activeTab, setActiveTab] = useState("profile")
  const [currentAccount, setCurrentAccount] = useState(account)
  const [isCredentialsModalOpen, setIsCredentialsModalOpen] = useState(false)
  const accountDomain = currentAccount.email.split("@")[1] ?? ""

  // Health check hook - watches account.health to auto-clear running state when ActionCable updates
  const { isRunning: isHealthCheckInProgress, triggerHealthCheck } =
    useHealthCheck({
      watchValue: currentAccount.health,
    })

  // Fetch fresh account data when the account prop changes (e.g., when sidepanel reopens)
  useEffect(() => {
    const fetchFreshAccount = async () => {
      try {
        const response = await fetch(`/email_accounts/${account.id}.json`, {
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          credentials: "same-origin",
        })

        if (response.ok) {
          const freshAccount = await response.json()
          setCurrentAccount(freshAccount)
        } else {
          // Fallback to prop if fetch fails
          setCurrentAccount(account)
        }
      } catch (error) {
        console.error("Failed to fetch fresh account data:", error)
        // Fallback to prop if fetch fails
        setCurrentAccount(account)
      }
    }

    // Only fetch if account ID changed (sidepanel reopened with different account)
    if (account.id !== currentAccount.id) {
      fetchFreshAccount()
    } else {
      // If same account, still update in case data changed
      setCurrentAccount(account)
    }
  }, [account.id, currentAccount.id])

  // Initialize form data with account data; kept in sync via effect below
  const [formData, setFormData] = useState({
    daily_limit:
      currentAccount.daily_messages_limit ?? currentAccount.limit ?? 0,
    default_account: currentAccount.default_account ?? false,
    sender_name:
      currentAccount.display_name ||
      currentAccount.owner_name ||
      currentAccount.email,
    signature: currentAccount.signature || "",
    managedMailbox: {
      first_name: currentAccount.managed_mailbox?.first_name || "",
      last_name: currentAccount.managed_mailbox?.last_name || "",
    },
  })

  // Keep form data in sync when the currentAccount changes
  useEffect(() => {
    setFormData({
      daily_limit:
        currentAccount.daily_messages_limit ?? currentAccount.limit ?? 0,
      default_account: currentAccount.default_account ?? false,
      sender_name:
        currentAccount.display_name ||
        currentAccount.owner_name ||
        currentAccount.email,
      signature: currentAccount.signature || "",
      managedMailbox: {
        first_name: currentAccount.managed_mailbox?.first_name || "",
        last_name: currentAccount.managed_mailbox?.last_name || "",
      },
    })
  }, [currentAccount])

  const handleRunHealthCheck = () => {
    triggerHealthCheck({ emailAccountIds: [currentAccount.id] })
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const formDataToSend = new FormData()
      formDataToSend.append(
        "email_account[daily_messages_limit]",
        formData.daily_limit.toString(),
      )
      formDataToSend.append(
        "email_account[default_account]",
        formData.default_account ? "1" : "0",
      )
      formDataToSend.append(
        "email_account[send_email_as]",
        currentAccount.email,
      )

      // Only include display_name if not Outlook and if it's different from email
      if (
        currentAccount.provider !== "outlook" &&
        formData.sender_name !== currentAccount.email
      ) {
        formDataToSend.append(
          "email_account[display_name]",
          formData.sender_name,
        )
      }

      // Include signature for SMTP/IMAP and managed accounts
      if (currentAccount.provider === "smtp_imap" || currentAccount.managed) {
        formDataToSend.append(
          "email_account[signature]",
          formData.signature || "",
        )
      }

      // Include managed_mailbox params for managed accounts
      if (currentAccount.managed) {
        if (formData.managedMailbox?.first_name) {
          formDataToSend.append(
            "managed_mailbox[first_name]",
            formData.managedMailbox.first_name,
          )
        }
        if (formData.managedMailbox?.last_name) {
          formDataToSend.append(
            "managed_mailbox[last_name]",
            formData.managedMailbox.last_name,
          )
        }
      }

      const response = await fetch(`/email_accounts/${currentAccount.id}`, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: formDataToSend,
      })

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }))
        throw new Error(
          error.error || i18n.t("js.email_accounts_center.errors.save_failed"),
        )
      }

      // Use the response from the server
      const updatedAccount = await response.json()

      // Update local state immediately
      setCurrentAccount(updatedAccount)

      // Notify parent component
      if (updatedAccount && onAccountUpdated) {
        onAccountUpdated(updatedAccount)
      }

      // Show success feedback (you might want to add a toast notification here)
    } catch (error) {
      // Show error feedback (you might want to add a toast notification here)
      console.error("Save failed:", error)
      alert(
        error instanceof Error
          ? error.message
          : i18n.t("js.email_accounts_center.errors.save_failed"),
      )
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <article>
      <div className="lead-details">
        <SheetHeader className="p-0">
          <header className="lead-details__header">
            <Avatar size="xxl">
              <AvatarImage src={currentAccount.owner_avatar_url} alt="" />
              <AvatarFallback>
                <img src={defaultLeadAvatarUrl} alt="" />
              </AvatarFallback>
            </Avatar>
            <div className="lead-details__heading">
              <SheetTitle className="lead-details__name mb-0">
                {currentAccount.email}
              </SheetTitle>
              <div className="lead-details__baseline">
                <span className="lead-details__baseline-item">
                  {currentAccount.owner_name ||
                    i18n.t("js.email_accounts_center.owner_placeholder") ||
                    ""}
                </span>
              </div>
            </div>
          </header>
        </SheetHeader>

        <div className="lead-details__actions mb-4 d-flex align-items-center gap-2">
          <Tag
            children={
              currentAccount.status === "active"
                ? i18n.t("js.email_accounts_center.status.active")
                : i18n.t("js.email_accounts_center.status.disconnected")
            }
            variant={currentAccount.status === "active" ? "success" : "danger"}
          />
          <Tag
            children={
              i18n.t(
                `js.email_accounts_center.provider.${currentAccount.provider}`,
              ) || "—"
            }
          />
        </div>

        <div className="lead-details__actions d-flex align-items-center gap-2 mb-4">
          <Button
            size="sm"
            type="button"
            onClick={handleRunHealthCheck}
            disabled={isHealthCheckInProgress}
            data-loading={isHealthCheckInProgress ? "true" : "none"}
          >
            <Icon
              icon="shield-check"
              variant="regular"
              size="sm"
              aria-hidden="true"
            />
            {i18n.t("js.email_accounts_center.actions.run_health_check")}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="default"
                size="sm"
                iconOnly
                type="button"
                aria-label={i18n.t(
                  "js.email_accounts_center.actions.more_actions_aria",
                )}
              >
                <Icon
                  icon="ellipsis-v"
                  variant="regular"
                  size="sm"
                  aria-hidden="true"
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="dropdown-menu-end">
              <DropdownMenuAction type="button">
                {i18n.t("js.email_accounts_center.actions.disconnect")}
              </DropdownMenuAction>
              {account.managed && (
                <DropdownMenuAction
                  type="button"
                  onClick={() => setIsCredentialsModalOpen(true)}
                >
                  {i18n.t("js.email_accounts_center.actions.credentials")}
                </DropdownMenuAction>
              )}
              <DropdownMenuAction type="button">
                {i18n.t("js.email_accounts_center.actions.cancel_renewal")}
              </DropdownMenuAction>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="h-tabs--lg mt-8"
        >
          <TabsList>
            <TabsListItem value="profile">
              <TabsTrigger value="profile">
                {i18n.t("js.email_accounts_center.email_account.profile.title")}
              </TabsTrigger>
            </TabsListItem>
            <TabsListItem value="usage">
              <TabsTrigger value="usage">
                {i18n.t("js.email_accounts_center.email_account.usage.title")}
              </TabsTrigger>
            </TabsListItem>
            <TabsListItem value="health">
              <TabsTrigger value="health">
                {i18n.t("js.email_accounts_center.email_account.health.title")}
              </TabsTrigger>
            </TabsListItem>
          </TabsList>

          <TabsContent value="profile" className="py-6">
            <EmailAccountProfileTab
              account={currentAccount}
              formData={formData}
              setFormData={setFormData}
            />
          </TabsContent>

          <TabsContent value="usage" className="p-6">
            <EmailAccountUsageTab accountId={currentAccount.id} />
          </TabsContent>

          <TabsContent value="health" className="p-0">
            {accountDomain && (
              <HealthTab
                domain={accountDomain}
                health={currentAccount.health ?? null}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
      {activeTab === "profile" && (
        <SheetFooter sticky={true}>
          <Button
            variant="primary"
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            data-loading={isSaving ? "true" : "none"}
          >
            {i18n.t("js.email_accounts_center.email_account.profile.save")}
          </Button>
        </SheetFooter>
      )}
      <CredentialsModal
        emailAccountId={account.id}
        email={account.email}
        isOpen={isCredentialsModalOpen}
        onClose={() => setIsCredentialsModalOpen(false)}
      />
    </article>
  )
}
